import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthContext';
import { COL } from '../../lib/collections';
import { getById, setDocData } from '../../lib/data/service';
import { utcNowIso } from '../../lib/format';
import { ROLE_META, JK_OPTIONS, DEFAULT_FAKULTAS } from '../../lib/constants';
import type { MahasiswaDoc, Role, UserDoc } from '../../lib/types';
import { Button } from '../../components/ui/Button';
import { Field, Input, Select } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toaster';
import { useAllJurusan, useAllProdi, useDosenUsers } from '../../lib/data/domains';
import { requestDosenPa, notifyUser } from '../../lib/data/mutations';

const REGISTER_ROLES = (Object.keys(ROLE_META) as Role[]).filter((r) => r !== 'admin');

export function OnboardingScreen() {
  const { uid, email, displayName, claims, claimsReady, refresh } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const jurusan = useAllJurusan().data.filter((j) => j.status === 'active');
  const prodi = useAllProdi().data.filter((p) => p.status === 'active');
  const dosens = useDosenUsers().data.filter((d) => d.status === 'active');
  const [name, setName] = useState(displayName ?? '');
  const [role, setRole] = useState<Role>('mahasiswa');
  const [jk, setJk] = useState('');
  const [telp, setTelp] = useState('');
  const [nim, setNim] = useState('');
  const [angkatan, setAngkatan] = useState(String(new Date().getFullYear()));
  const [jurusanId, setJurusanId] = useState('');
  const [prodiId, setProdiId] = useState('');
  const [paDosenId, setPaDosenId] = useState('');
  const [busy, setBusy] = useState(false);
  const prodiInJurusan = jurusanId ? prodi.filter((p) => p.jurusanId === jurusanId) : [];

  // Users carrying the `admin` custom claim are provisioned as admins directly —
  // no role selection needed (they were promoted via promote.py).
  const provisioningAsAdmin = Boolean(claims.admin);

  // Wait for the ID token claims to settle so a promoted admin never briefly
  // sees the role-selection form.
  if (!claimsReady) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-[460px] flex-col justify-center px-6">
        <div className="card p-6 text-center">
          <p className="text-[14px] text-muted">Menyiapkan akun…</p>
        </div>
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uid) return;
    const finalRole: Role = provisioningAsAdmin ? 'admin' : role;
    if (finalRole === 'mahasiswa' && !jurusanId) {
      toast('Pilih jurusan', { body: 'Mahasiswa wajib memilih jurusan.', tone: 'warning' });
      return;
    }
    if (finalRole === 'mahasiswa' && !prodiId) {
      toast('Pilih program studi', {
        body: 'Mahasiswa wajib memilih program studi di bawah jurusan.',
        tone: 'warning',
      });
      return;
    }
    if (finalRole === 'mahasiswa' && !angkatan.trim()) {
      toast('Isi angkatan', { body: 'Mahasiswa wajib mengisi tahun angkatan.', tone: 'warning' });
      return;
    }
    setBusy(true);
    try {
      const now = utcNowIso();
      // Guard: an existing user who somehow lands here (e.g. a lookup miss)
      // must never have their role/status wiped by a blind overwrite.
      const existingUser = await getById<UserDoc>(COL.users, uid);
      if (existingUser) {
        if (existingUser.role === 'mahasiswa' && nim) {
          const existingMhs = await getById<MahasiswaDoc>(COL.mahasiswa, uid);
          if (!existingMhs) {
            const selected = prodi.find((p) => p.id === prodiId);
            const selectedJurusan = jurusan.find((j) => j.id === selected?.jurusanId);
            await setDocData(
              COL.mahasiswa,
              uid,
              {
                id: uid,
                userId: uid,
                nim,
                name,
                prodi: selected?.name ?? '',
                prodiId: selected?.id,
                jurusan: selectedJurusan?.name ?? '',
                jurusanId: selected?.jurusanId,
                fakultas: DEFAULT_FAKULTAS,
                angkatan: angkatan.trim(),
                status: 'aktif',
                telp: telp || undefined,
                createdAt: now,
                updatedAt: now,
              },
              { merge: false },
            );
          }
        }
        toast('Profil sudah ada — selamat datang kembali', { tone: 'success' });
        refresh();
        navigate('/', { replace: true });
        return;
      }
      // Mahasiswa (and promoted admins) are active immediately; dosen & staf
      // wait for admin approval via the user management panel.
      const status = provisioningAsAdmin || finalRole === 'mahasiswa' ? 'active' : 'pending';
      const selected = prodi.find((p) => p.id === prodiId);
      const selectedJurusan = jurusan.find((j) => j.id === selected?.jurusanId);

      await setDocData(
        COL.users,
        uid,
        {
          id: uid,
          uid,
          email: email ?? '',
          name,
          role: finalRole,
          status,
          jk: jk || undefined,
          prodi: finalRole === 'mahasiswa' ? (selected?.name ?? '') : '',
          fakultas: DEFAULT_FAKULTAS,
          createdAt: now,
          updatedAt: now,
        },
        { merge: false },
      );
      if (finalRole === 'mahasiswa' && nim) {
        await setDocData(
          COL.mahasiswa,
          uid,
          {
            id: uid,
            userId: uid,
            nim,
            name,
            prodi: selected?.name ?? '',
            prodiId: selected?.id,
            jurusan: selectedJurusan?.name ?? '',
            jurusanId: selected?.jurusanId,
            fakultas: DEFAULT_FAKULTAS,
            angkatan: angkatan.trim(),
            status: 'aktif',
            telp: telp || undefined,
            createdAt: now,
            updatedAt: now,
          },
          { merge: false },
        );
      }
      // Optional Dosen PA selection — the dosen must verify before the link
      // becomes active (see Bimbingan → Konsultasi PA).
      if (finalRole === 'mahasiswa' && paDosenId) {
        await requestDosenPa({ mahasiswaId: uid, dosenId: paDosenId, requestedBy: uid });
        const pa = dosens.find((d) => d.id === paDosenId);
        await notifyUser(paDosenId, {
          title: 'Permintaan menjadi Dosen PA',
          body: `${name} (${nim}) meminta Anda menjadi Dosen Pembimbing Akademiknya.`,
          type: 'info',
          link: `/app/dosen/bimbingan`,
        });
        toast('Permintaan Dosen PA dikirim', {
          body: `Menunggu verifikasi ${pa?.name ?? 'dosen'} sebelum menjadi Dosen PA Anda.`,
          tone: 'success',
        });
      }
      if (status === 'pending') {
        toast('Akun menunggu persetujuan', {
          body: 'Administrator akan mengaktifkan akun Anda pada panel pengguna.',
          tone: 'warning',
        });
      } else {
        toast('Profil dibuat', {
          body: 'Silakan lengkapi data di halaman profil.',
          tone: 'success',
        });
      }
      refresh();
      navigate('/', { replace: true });
    } catch (err) {
      toast('Gagal membuat profil', { body: String(err), tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[460px] flex-col justify-center px-6 pb-10">
      <div className="card p-6">
        <h1 className="display text-[22px] font-bold text-ink">Lengkapi profil Anda</h1>
        <p className="mt-1 text-[14px] text-muted">
          {provisioningAsAdmin
            ? 'Akun Anda terdaftar sebagai administrator dan langsung aktif.'
            : 'Pilih peran akademik Anda. Mahasiswa langsung aktif; dosen & staf menunggu persetujuan admin.'}
        </p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <Field label="Nama lengkap">
            <Input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nama Anda"
            />
          </Field>
          {provisioningAsAdmin ? (
            <Field
              label="Peran"
              hint="Peran administrator diberikan melalui Firebase custom claim."
            >
              <Input value="Administrator" disabled />
            </Field>
          ) : (
            <>
              <Field label="Peran" hint="Pilih sesuai peran akademik Anda.">
                <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                  {REGISTER_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_META[r].label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Jenis kelamin (opsional)">
                <Select value={jk} onChange={(e) => setJk(e.target.value)}>
                  <option value="">— Pilih jenis kelamin —</option>
                  {JK_OPTIONS.map((j) => (
                    <option key={j} value={j}>
                      {j}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}
          {role === 'mahasiswa' && !provisioningAsAdmin && (
            <>
              <Field label="NIM">
                <Input
                  required
                  value={nim}
                  onChange={(e) => setNim(e.target.value)}
                  placeholder="2021xxxxx"
                  inputMode="numeric"
                />
              </Field>
              <Field label="Angkatan" hint="Tahun angkatan masuk, contoh: 2021.">
                <Input
                  required
                  value={angkatan}
                  onChange={(e) => setAngkatan(e.target.value)}
                  placeholder="2021"
                  inputMode="numeric"
                />
              </Field>
              <Field label="No. HP (opsional)">
                <Input
                  value={telp}
                  onChange={(e) => setTelp(e.target.value)}
                  placeholder="08xxxxxxxxxx"
                  inputMode="tel"
                />
              </Field>
              <Field
                label="Jurusan"
                hint="Pilih jurusan terlebih dahulu, lalu program studi di bawahnya."
              >
                <Select
                  value={jurusanId}
                  onChange={(e) => {
                    setJurusanId(e.target.value);
                    setProdiId('');
                  }}
                >
                  <option value="">— Pilih jurusan —</option>
                  {jurusan.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Program studi"
                hint="Program studi menentukan kaprodi, staf, dan dosen pembimbing Anda."
              >
                <Select
                  value={prodiId}
                  onChange={(e) => setProdiId(e.target.value)}
                  disabled={!jurusanId}
                >
                  <option value="">
                    {jurusanId ? '— Pilih program studi —' : 'Pilih jurusan terlebih dahulu'}
                  </option>
                  {prodiInJurusan.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Dosen PA (opsional)"
                hint="Permintaan harus disetujui dosen yang bersangkutan sebelum menjadi Dosen PA Anda."
              >
                <Select value={paDosenId} onChange={(e) => setPaDosenId(e.target.value)}>
                  <option value="">— Pilih dosen (bisa diisi nanti) —</option>
                  {dosens.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}
          <Button block type="submit" disabled={busy}>
            {busy ? 'Menyimpan…' : 'Simpan & lanjutkan'}
          </Button>
          {uid && (
            <p className="mt-3 text-center text-[11px] leading-relaxed text-faint">
              UID login: <span className="font-mono font-semibold text-muted">{uid}</span>
              <br />
              Samakan dengan field <span className="font-mono">uid</span> pada dokumen Anda di tabel{' '}
              <span className="font-mono">bimbingan_users</span>.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
